import { Checkbox } from '@trellis/roots/checkbox'
import { Label } from '@trellis/roots/label'
import { ConfirmDialog } from '@trellis/roots/overlays'
import { TriangleAlert } from 'lucide-react'
import { useId, useState } from 'react'
import { toast } from 'sonner'

import { EmailDivertNotice } from '@/components/email'
import {
  ReminderRateLimitedError,
  useSendReminderEmailMutation,
} from '@/hooks/useInvoices'
import { formatCalendarDate } from '@/lib/format'
import { cn } from '@/lib/utils'

import { AMBER_NOTICE_CLASSES } from './reminders.constants'

export interface SendReminderNowTarget {
  invoiceId: string
  invoiceNumber: string
  clientName: string
  /** Shown in the confirmation when known (the invoice page has it, lists don't). */
  clientEmail?: string | null
  /** Midnight-UTC date of the next scheduled client email, if any. */
  nextReminderDate: string | null
}

interface SendReminderNowDialogProps {
  /** Null keeps the dialog closed. */
  target: SendReminderNowTarget | null
  onOpenChange: (isOpen: boolean) => void
  /** Called after a send, or when the server says the 24-hour limit is in force. */
  onPaused?: (invoiceId: string) => void
}

/** Confirms a one-off reminder email, warning that scheduled reminders still go out. */
export function SendReminderNowDialog({
  target,
  onOpenChange,
  onPaused,
}: SendReminderNowDialogProps) {
  const checkboxId = useId()
  const sendMutation = useSendReminderEmailMutation()
  const [shouldDismissNext, setShouldDismissNext] = useState(false)

  function handleOpenChange(isOpen: boolean) {
    if (!isOpen) setShouldDismissNext(false)
    onOpenChange(isOpen)
  }

  async function handleSend() {
    if (!target) return
    const dismissNextReminder = shouldDismissNext && target.nextReminderDate !== null
    try {
      await sendMutation.mutateAsync({
        id: target.invoiceId,
        data: { dismissNextReminder },
      })
      toast.success(
        `Reminder sent to ${target.clientName}. ${dismissNextReminder ? 'Next reminder dismissed.' : 'Scheduled reminders unchanged.'}`,
      )
      onPaused?.(target.invoiceId)
      setShouldDismissNext(false)
      onOpenChange(false)
    } catch (caughtError) {
      // Retrying can't succeed inside the 24 hours, so close and leave Send now paused.
      if (caughtError instanceof ReminderRateLimitedError) {
        onPaused?.(target.invoiceId)
        setShouldDismissNext(false)
        onOpenChange(false)
      }
      toast.error('Failed to send reminder', {
        description: caughtError instanceof Error ? caughtError.message : undefined,
      })
    }
  }

  const recipient = target?.clientEmail
    ? `${target.clientName} (${target.clientEmail})`
    : target?.clientName

  return (
    <ConfirmDialog
      open={target !== null}
      onOpenChange={handleOpenChange}
      title="Send a reminder now?"
      description={
        <>
          This emails {recipient} a payment reminder for{' '}
          <strong>{target?.invoiceNumber}</strong> right away.
        </>
      }
      confirmLabel={sendMutation.isPending ? 'Sending…' : 'Send reminder now'}
      isPending={sendMutation.isPending}
      closeOnConfirm={false}
      onConfirm={() => void handleSend()}
    >
      <EmailDivertNotice />
      <div
        className={cn(
          'flex gap-2.5 rounded-[10px] border p-3 text-sm',
          AMBER_NOTICE_CLASSES,
        )}
      >
        <TriangleAlert className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
        <p>
          {target?.nextReminderDate
            ? `This does not use up the next scheduled reminder. ${target.clientName} will still get the one on ${formatCalendarDate(target.nextReminderDate, { weekday: 'short', day: 'numeric', month: 'short' })} at 9:00 am.`
            : 'No other reminders are scheduled for this invoice.'}{' '}
          You can only send one reminder every 24 hours.
        </p>
      </div>
      {target?.nextReminderDate && (
        <div className="flex items-center gap-2.5">
          <Checkbox
            id={checkboxId}
            checked={shouldDismissNext}
            onCheckedChange={(isChecked) => setShouldDismissNext(isChecked === true)}
          />
          <Label htmlFor={checkboxId} className="font-normal">
            Also dismiss the next scheduled reminder
          </Label>
        </div>
      )}
    </ConfirmDialog>
  )
}
